import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const inputCls = "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333]";
const TSHIRT = ["XS", "S", "M", "L", "XL", "XXL", "XXXL"];
const EDIT_KEYS = ["full_name", "gender", "date_of_birth", "mobile", "email", "tshirt_size"];
const err = (e, f) => formatApiErrorDetail(e?.response?.data?.detail) || f;

const L = ({ label, children }) => (
  <label className="block"><div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-1.5">{label}</div>{children}</label>
);

export function EditRegDialog({ reg, onClose, onSaved }) {
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (reg) setF(Object.fromEntries(EDIT_KEYS.map((k) => [k, reg[k] || ""]))); }, [reg]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: k === "mobile" ? e.target.value.replace(/\D/g, "").slice(0, 10) : e.target.value }));
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put(`/admin/marathon/registrations/${reg.id}`, f); toast.success("Registration updated"); onSaved(data); }
    catch (e) { toast.error(err(e, "Update failed")); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!reg} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="mreg-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Edit {reg?.registration_no}</DialogTitle>
          <DialogDescription>Run, fee, payment and bib details can't be edited. The age limit is still checked.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><L label="Full name"><input className={inputCls} value={f.full_name || ""} maxLength={30} onChange={set("full_name")} data-testid="mreg-edit-name" /></L></div>
          <L label="Gender"><select className={inputCls} value={f.gender || ""} onChange={set("gender")} data-testid="mreg-edit-gender">{["Male", "Female", "Other"].map((g) => <option key={g}>{g}</option>)}</select></L>
          <L label="Date of birth"><input type="date" className={inputCls} value={f.date_of_birth || ""} onChange={set("date_of_birth")} data-testid="mreg-edit-dob" /></L>
          <L label="Mobile"><input className={inputCls} value={f.mobile || ""} onChange={set("mobile")} data-testid="mreg-edit-mobile" /></L>
          <L label="T-shirt"><select className={inputCls} value={f.tshirt_size || ""} onChange={set("tshirt_size")} data-testid="mreg-edit-tshirt">{TSHIRT.map((s) => <option key={s}>{s}</option>)}</select></L>
          <div className="col-span-2"><L label="Email"><input className={inputCls} value={f.email || ""} maxLength={30} onChange={set("email")} data-testid="mreg-edit-email" /></L></div>
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} className="px-4 py-2.5 text-sm" data-testid="mreg-edit-cancel">Cancel</button>
          <button type="button" onClick={save} disabled={saving} className="bg-[#234333] text-white px-5 py-2.5 text-sm font-semibold rounded-sm disabled:opacity-60" data-testid="mreg-edit-save">{saving ? "Saving…" : "Save changes"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteRegDialog({ reg, onClose, onDeleted }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => setReason(""), [reg]);
  const del = async () => {
    setBusy(true);
    try { await api.post(`/admin/marathon/registrations/${reg.id}/delete`, { reason }); toast.success("Registration moved to Deleted archive"); onDeleted(); }
    catch (e) { toast.error(err(e, "Delete failed")); }
    finally { setBusy(false); }
  };
  const paid = reg?.payment_status === "PAID";
  return (
    <Dialog open={!!reg} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="mreg-delete-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Delete {reg?.registration_no}?</DialogTitle>
          <DialogDescription>{reg?.full_name} · {reg?.run_name}. The record and its payment details are kept in the Deleted archive.</DialogDescription>
        </DialogHeader>
        {paid && <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-sm p-3" data-testid="mreg-delete-paid-warning">This registration is PAID (bib {reg.bib_number}). It will be flagged "Refund required" in the archive.</div>}
        <L label="Reason (required)"><textarea rows={3} className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} data-testid="mreg-delete-reason" /></L>
        <DialogFooter>
          <button type="button" onClick={onClose} className="px-4 py-2.5 text-sm" data-testid="mreg-delete-cancel">Cancel</button>
          <button type="button" onClick={del} disabled={busy || reason.trim().length < 5} className="bg-red-700 text-white px-5 py-2.5 text-sm font-semibold rounded-sm disabled:opacity-50" data-testid="mreg-delete-confirm">{busy ? "Deleting…" : "Delete"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeletedList() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/marathon/registrations-deleted").then(({ data }) => setItems(data)).catch(() => setItems([])); }, []);
  if (!items) return <div className="text-sm text-[#6b655b]">Loading…</div>;
  return (
    <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
      <table className="w-full text-sm" data-testid="mreg-deleted-table">
        <thead className="bg-[#f9f8f6] text-[10px] uppercase tracking-wider text-[#6b655b]">
          <tr>{["Reg No.", "Name", "Run", "Fee", "Status", "Paid Order", "Deleted", "By", "Reason"].map((h) => <th key={h} className="text-left p-3">{h}</th>)}</tr>
        </thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-[#6b655b]" data-testid="mreg-deleted-empty">No deleted registrations</td></tr>}
          {items.map((r) => (
            <tr key={r.id} className="border-t border-[#e5e0d8]" data-testid={`mreg-deleted-row-${r.id}`}>
              <td className="p-3 font-mono text-xs">{r.registration_no}</td>
              <td className="p-3">{r.full_name}<div className="text-[11px] text-[#6b655b]">{r.mobile}</div></td>
              <td className="p-3">{r.distance_km} KM</td>
              <td className="p-3">₹{r.fee}</td>
              <td className="p-3">{r.payment_status}{r.refund_required && <div className="text-[10px] font-bold text-red-700 uppercase">Refund required</div>}</td>
              <td className="p-3 font-mono text-xs">{r.paid_order_id || "—"}</td>
              <td className="p-3 text-xs">{new Date(r.deleted_at).toLocaleString()}</td>
              <td className="p-3 text-xs">{r.deleted_by}</td>
              <td className="p-3 text-xs max-w-[240px]">{r.delete_reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
